import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { landingPageTemplatesApi } from '@/api/landingPageTemplates'
import { useToast } from '@/hooks/useToast'
import { getDisplayErrorMessages, isValidationError } from '@/utils/errorHandling'
import type { LandingPageTemplateListItemDto } from '@/types/landingPage'

export interface UseLandingPageTemplatesResult {
    /** Null until the first load lands, so "not loaded" is never read as "no templates". */
    templates: LandingPageTemplateListItemDto[] | null
    loading: boolean
    /** The last load's failure, for the page to show in place of the list. Not toasted. */
    error: string | null
    reload: () => Promise<void>
    /** Renames the template on `webId`. Resolves false when refused; the failure has been shown. */
    renameTemplate: (webId: string, name: string) => Promise<boolean>
    /** Removes the template on `webId`; the landing page is unchanged. Resolves false when refused. */
    removeTemplate: (webId: string) => Promise<boolean>
}

/**
 * The workspace's landing-page templates (ORB-1103), for the Templates page and the builder's picker. With
 * `forWebId`, only the ones a page on that web can start from, as the server judges it.
 */
export function useLandingPageTemplates(workspaceId: string, forWebId?: string): UseLandingPageTemplatesResult {
    const { showError } = useToast()
    const [templates, setTemplates] = useState<LandingPageTemplateListItemDto[] | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // Only the newest load may write, so a load for the previous workspace cannot land over this one's.
    const requestIdRef = useRef(0)

    const reload = useCallback(async () => {
        const requestId = ++requestIdRef.current
        setLoading(true)
        setError(null)
        const res = await landingPageTemplatesApi.listTemplates(workspaceId, forWebId)
        if (requestIdRef.current !== requestId) return
        if (res.data) setTemplates(res.data)
        else setError(getDisplayErrorMessages(res.error, 'Unable to load templates').join(' '))
        setLoading(false)
    }, [workspaceId, forWebId])

    useEffect(() => {
        setTemplates(null)
        void reload()
        return () => { requestIdRef.current++ }
    }, [reload])

    // A 422 is the server's refusal, which the global handler has already shown in its own words.
    const reportFailure = useCallback((res: { error?: string; status?: number }, title: string, fallback: string) => {
        if (isValidationError(res.status)) return
        showError(title, getDisplayErrorMessages(res.error, fallback).join(' '))
    }, [showError])

    const renameTemplate = useCallback(async (webId: string, name: string) => {
        const res = await landingPageTemplatesApi.saveTemplate(workspaceId, webId, { Name: name })
        const template = res.data?.Template
        if (!template) {
            reportFailure(res, 'Could not rename the template', 'Unable to rename the template')
            return false
        }
        setTemplates(current => current?.map(item => item.WebId === webId ? { ...item, Template: template } : item) ?? current)
        return true
    }, [workspaceId, reportFailure])

    const removeTemplate = useCallback(async (webId: string) => {
        const res = await landingPageTemplatesApi.removeTemplate(workspaceId, webId)
        if (res.status !== 204 && res.status !== 200) {
            reportFailure(res, 'Could not remove the template', 'Unable to remove the template')
            return false
        }
        setTemplates(current => current?.filter(item => item.WebId !== webId) ?? current)
        return true
    }, [workspaceId, reportFailure])

    return { templates, loading, error, reload, renameTemplate, removeTemplate }
}
